"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordDataSchemaMap = void 0;
const zod_1 = require("zod");
const recordType_1 = require("./recordType");
/**
 * Base schema for validating Altium record data objects
 */
const baseRecordDataObjectSchema = zod_1.z.object({
    RECORD: zod_1.z.coerce.number(),
    UNIQUEID: zod_1.z.string().trim().optional(),
    OWNERINDEX: zod_1.z.coerce.number().optional(),
});
const withSinglePointSchema = zod_1.z.object({
    "LOCATION.X": zod_1.z.coerce.number(),
    "LOCATION.Y": zod_1.z.coerce.number(),
    "LOCATION.X_FRAC": zod_1.z.coerce.number().optional().default(0),
    "LOCATION.Y_FRAC": zod_1.z.coerce.number().optional().default(0),
});
const withMultiPointSchema = zod_1.z.object({
    LOCATIONCOUNT: zod_1.z.coerce.number().int(),
});
const withRecordTypeSchema = (recordType) => zod_1.z.object({
    RECORD: zod_1.z.literal(`${recordType}`).transform(() => recordType),
});
const withComponentChildSchema = zod_1.z.object({
    OWNERPARTID: zod_1.z.coerce.number().optional().default(-1),
    OWNERPARTDISPLAYMODE: zod_1.z.coerce.number().optional().default(0),
});
/**
 * Mapping of Altium record types to their corresponding Zod schemas
 */
exports.recordDataSchemaMap = {
    // Root record type
    [recordType_1.AltiumRecordType.ROOT]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.ROOT).shape,
    }),
    // Supported record types
    [recordType_1.AltiumRecordType.WIRE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.WIRE).shape,
        ...withMultiPointSchema.shape,
    }),
    [recordType_1.AltiumRecordType.JUNCTION]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.JUNCTION).shape,
        ...withSinglePointSchema.shape,
    }),
    [recordType_1.AltiumRecordType.DESIGNATOR]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.DESIGNATOR).shape,
        ...withSinglePointSchema.shape,
        ...{
            TEXT: zod_1.z.string().trim(),
        },
    }),
    [recordType_1.AltiumRecordType.PARAMETER]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.PARAMETER).shape,
        ...{
            NAME: zod_1.z.string().toUpperCase(),
            TEXT: zod_1.z.string().optional(),
        },
    }),
    [recordType_1.AltiumRecordType.POWER_PORT]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.POWER_PORT).shape,
        ...withSinglePointSchema.shape,
        ...{
            TEXT: zod_1.z.string().trim(),
            ORIENTATION: zod_1.z.coerce.number().int().min(1).max(4).default(4),
        },
    }),
    [recordType_1.AltiumRecordType.PORT]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.PORT).shape,
        ...withSinglePointSchema.shape,
        ...{
            NAME: zod_1.z.string().trim(),
        },
    }),
    [recordType_1.AltiumRecordType.COMPONENT]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.COMPONENT).shape,
        ...withSinglePointSchema.shape,
        ...{
            LIBREFERENCE: zod_1.z.string().trim(),
            DESIGNITEMID: zod_1.z.string().trim().optional(),
            COMPONENTDESCRIPTION: zod_1.z.string().trim().optional(),
            LIBRARYPATH: zod_1.z.string().trim().optional(),
            SOURCELIBRARYNAME: zod_1.z.string().trim().optional(),
            SHEETPARTFILENAME: zod_1.z.string().trim().optional(),
            PARTCOUNT: zod_1.z.coerce.number(),
            DISPLAYMODE: zod_1.z.coerce.number().optional().default(0),
            CURRENTPARTID: zod_1.z.coerce.number().optional().default(-1),
            ORIENTATION: zod_1.z.coerce.number().int().min(0).optional().default(0),
            ISMIRRORED: zod_1.z
                .union([zod_1.z.literal("F"), zod_1.z.literal("T")])
                .optional()
                .default("F"),
        },
    }),
    [recordType_1.AltiumRecordType.PIN]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.PIN).shape,
        ...withSinglePointSchema.shape,
        ...withComponentChildSchema.shape,
        ...{
            NAME: zod_1.z.string().trim().optional().default(""),
            DESIGNATOR: zod_1.z.string().trim().optional().default(""),
            PINLENGTH: zod_1.z.coerce.number().optional().default(0),
            PINLENGTH_FRAC: zod_1.z.coerce.number().optional().default(0),
            PINCONGLOMERATE: zod_1.z.coerce.number().optional().default(0),
        },
    }),
    [recordType_1.AltiumRecordType.NET_LABEL]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.NET_LABEL).shape,
        ...withSinglePointSchema.shape,
        ...{
            TEXT: zod_1.z.string().trim(),
        },
    }),
    // Supported SVG record types
    [recordType_1.AltiumRecordType.POLYLINE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.POLYLINE).shape,
        ...withComponentChildSchema.shape,
        ...withMultiPointSchema.shape,
        ...{
            LINESTYLE: zod_1.z.coerce.number().optional().default(0),
            LINESTYLEEXT: zod_1.z.coerce.number().optional().default(0),
        },
    }),
    [recordType_1.AltiumRecordType.POLYGON]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.POLYGON).shape,
        ...withComponentChildSchema.shape,
        ...withMultiPointSchema.shape,
    }),
    [recordType_1.AltiumRecordType.ELLIPSE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.ELLIPSE).shape,
        ...withSinglePointSchema.shape,
        ...withComponentChildSchema.shape,
        ...{
            RADIUS: zod_1.z.coerce.number().optional().default(0),
            RADIUS_FRAC: zod_1.z.coerce.number().optional().default(0),
            SECONDARYRADIUS: zod_1.z.coerce.number().optional().default(0),
            SECONDARYRADIUS_FRAC: zod_1.z.coerce.number().optional().default(0),
        },
    }),
    [recordType_1.AltiumRecordType.RECTANGLE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.RECTANGLE).shape,
        ...withComponentChildSchema.shape,
        ...withSinglePointSchema.shape,
        ...{
            "CORNER.X": zod_1.z.coerce.number().default(0),
            "CORNER.Y": zod_1.z.coerce.number().default(0),
            "CORNER.X_FRAC": zod_1.z.coerce.number().optional().default(0),
            "CORNER.Y_FRAC": zod_1.z.coerce.number().optional().default(0),
        },
    }),
    [recordType_1.AltiumRecordType.ARC]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.ARC).shape,
        ...withSinglePointSchema.shape,
        ...withComponentChildSchema.shape,
        ...{
            RADIUS: zod_1.z.coerce.number().optional().default(0),
            RADIUS_FRAC: zod_1.z.coerce.number().optional().default(0),
            STARTANGLE: zod_1.z.coerce.number().optional().default(0),
            ENDANGLE: zod_1.z.coerce.number().optional().default(0),
        },
    }),
    [recordType_1.AltiumRecordType.LINE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.LINE).shape,
        ...withComponentChildSchema.shape,
        ...withSinglePointSchema.shape,
        ...{
            "CORNER.X": zod_1.z.coerce.number().default(0),
            "CORNER.Y": zod_1.z.coerce.number().default(0),
            "CORNER.X_FRAC": zod_1.z.coerce.number().optional().default(0),
            "CORNER.Y_FRAC": zod_1.z.coerce.number().optional().default(0),
        },
    }),
    // Unsupported record types
    [recordType_1.AltiumRecordType.BEZIER]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.BEZIER).shape,
        ...withComponentChildSchema.shape,
    }),
    [recordType_1.AltiumRecordType.PIECHART]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.PIECHART).shape,
        ...withComponentChildSchema.shape,
    }),
    [recordType_1.AltiumRecordType.ROUND_RECTANGLE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.ROUND_RECTANGLE).shape,
        ...withComponentChildSchema.shape,
    }),
    [recordType_1.AltiumRecordType.ELLIPTICAL_ARC]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.ELLIPTICAL_ARC).shape,
        ...withComponentChildSchema.shape,
    }),
    [recordType_1.AltiumRecordType.IEEE_SYMBOL]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.IEEE_SYMBOL).shape,
        ...withComponentChildSchema.shape,
    }),
    [recordType_1.AltiumRecordType.UNKNOWN]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.UNKNOWN).shape,
    }),
    [recordType_1.AltiumRecordType.HEADER]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.HEADER).shape,
    }),
    [recordType_1.AltiumRecordType.LABEL]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.LABEL).shape,
    }),
    [recordType_1.AltiumRecordType.SHEET_SYMBOL]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.SHEET_SYMBOL).shape,
    }),
    [recordType_1.AltiumRecordType.SHEET_ENTRY]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.SHEET_ENTRY).shape,
    }),
    [recordType_1.AltiumRecordType.NO_ERC]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.NO_ERC).shape,
    }),
    [recordType_1.AltiumRecordType.BUS]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.BUS).shape,
    }),
    [recordType_1.AltiumRecordType.TEXT_FRAME]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.TEXT_FRAME).shape,
    }),
    [recordType_1.AltiumRecordType.IMAGE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.IMAGE).shape,
    }),
    [recordType_1.AltiumRecordType.SHEET]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.SHEET).shape,
    }),
    [recordType_1.AltiumRecordType.SHEET_NAME]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.SHEET_NAME).shape,
    }),
    [recordType_1.AltiumRecordType.FILE_NAME]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.FILE_NAME).shape,
    }),
    [recordType_1.AltiumRecordType.BUS_ENTRY]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.BUS_ENTRY).shape,
    }),
    [recordType_1.AltiumRecordType.TEMPLATE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.TEMPLATE).shape,
    }),
    [recordType_1.AltiumRecordType.PARAMETER_SET]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.PARAMETER_SET).shape,
    }),
    [recordType_1.AltiumRecordType.IMPLEMENTATION_LIST]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.IMPLEMENTATION_LIST).shape,
    }),
    [recordType_1.AltiumRecordType.IMPLEMENTATION]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.IMPLEMENTATION).shape,
    }),
    [recordType_1.AltiumRecordType.MAP_DEFINER_LIST]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.MAP_DEFINER_LIST).shape,
    }),
    [recordType_1.AltiumRecordType.MAP_DEFINER]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.MAP_DEFINER).shape,
    }),
    [recordType_1.AltiumRecordType.IMPL_PARAMS]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.IMPL_PARAMS).shape,
    }),
    [recordType_1.AltiumRecordType.NOTE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.NOTE).shape,
    }),
    [recordType_1.AltiumRecordType.COMPILE_MASK]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.COMPILE_MASK).shape,
    }),
    [recordType_1.AltiumRecordType.HARNESS_CONNECTOR]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.HARNESS_CONNECTOR).shape,
    }),
    [recordType_1.AltiumRecordType.HARNESS_ENTRY]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.HARNESS_ENTRY).shape,
    }),
    [recordType_1.AltiumRecordType.HARNESS_TYPE]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.HARNESS_TYPE).shape,
    }),
    [recordType_1.AltiumRecordType.SIGNAL_HARNESS]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.SIGNAL_HARNESS).shape,
    }),
    [recordType_1.AltiumRecordType.BLANKET]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.BLANKET).shape,
    }),
    [recordType_1.AltiumRecordType.HYPERLINK]: zod_1.z.object({
        ...baseRecordDataObjectSchema.shape,
        ...withRecordTypeSchema(recordType_1.AltiumRecordType.HYPERLINK).shape,
    }),
};
//# sourceMappingURL=recordSchemas.js.map